import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import { QuoteValidationError } from "./quoteValidation";

/**
 * Quote flow decision - what action to take based on pricing record
 */
export enum QuoteFlowDecision {
  CREATE_NEW_QUOTE = "CREATE_NEW_QUOTE",
  SHOW_EXISTING_QUOTE = "SHOW_EXISTING_QUOTE",
  SHOW_ERROR = "SHOW_ERROR",
}

/**
 * Customer details extracted from pricing record
 */
export interface CustomerDetails {
  customerName: string | undefined;
  customerEmail: string | undefined;
  potentialId: string | undefined;
  planType: string | undefined;
  location: string | undefined;
  stage: string | undefined;
}

/**
 * Determines what action to take based on pricing record status and validation
 * @param pricingRecord - The pricing record from the API
 * @param validationError - The validation result
 * @returns The flow decision enum
 */
export function determineQuoteFlow(
  pricingRecord: IPricingRecord,
  validationError: QuoteValidationError
): QuoteFlowDecision {
  // Validation errors take precedence
  if (validationError !== QuoteValidationError.VALID) {
    return QuoteFlowDecision.SHOW_ERROR;
  }

  // New quotes need to go through the creation flow
  if (pricingRecord.status === "New") {
    return QuoteFlowDecision.CREATE_NEW_QUOTE;
  }

  // Existing quotes show their status
  return QuoteFlowDecision.SHOW_EXISTING_QUOTE;
}

/**
 * Extracts customer details from pricing record
 * @param pricingRecord - The pricing record from the API
 * @returns Customer details object
 */
export function extractCustomerDetails(
  pricingRecord: IPricingRecord
): CustomerDetails {
  return {
    customerName: pricingRecord.zoho_potential?.Contact_Name?.name,
    customerEmail: pricingRecord.zoho_contact?.Email,
    potentialId: pricingRecord.zoho_potential?.id,
    planType: pricingRecord.zoho_potential?.PlanPreference,
    location: pricingRecord.zoho_contact?.Location,
    stage: pricingRecord.zoho_potential?.Stage,
  };
}
